// FILE: NewThreadDialogPullRequestField.tsx
// Purpose: The "Pull request" combobox inside NewThreadDialog — lists a project's open pull
//          requests so a thread can be opened on one instead of on a fresh branch.
// Layer: Web components
// Exports: NewThreadDialogPullRequestField
//
// Twin of `NewThreadDialogPlaneIssueField`, and split out for the same reason: it owns an async
// read whose lifecycle is per-project and lazy on open, which would tangle with the dialog's own
// per-open reset.
//
// Two things it does differently from that twin:
//
//   - Open pull requests only. Opening a thread on a merged branch is not the workflow this
//     serves, and offering hundreds of closed ones would bury the handful that matter.
//   - It reads through react-query rather than a bare fetch, so it shares the section's cache:
//     someone who just came from the Pull Requests list pays nothing to open this. Polling is
//     turned off here — a picker that is open for ten seconds has no business spending the
//     token's rate budget on a timer.
//
// A pull request that already has a thread is shown, not hidden, with a mark saying so. Opening
// a second thread on the same branch is unusual but legitimate — a fresh start after abandoning
// the first — and silently dropping the row would look like the pull request did not exist.

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import type { ProjectId, PullRequestListEntry } from "@taskin-code/contracts";

import { LoaderIcon } from "~/lib/icons";
import { pullRequestsListQueryOptions } from "~/lib/pullRequestsReactQuery";
import { readPullRequestErrorMessage } from "./pullRequests/pullRequestPresentation";

import type { NewThreadDialogPullRequest } from "./NewThreadDialog.logic";
import {
  Combobox,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxPopup,
  ComboboxTrigger,
} from "./ui/combobox";
import { Button } from "./ui/button";

const NO_PULL_REQUEST = "__no_pull_request__";

/** How many to offer. One page is plenty for a picker; the section itself is where you browse. */
const PICKER_PER_PAGE = 50;

export interface NewThreadDialogPullRequestFieldProps {
  projectId: ProjectId;
  value: NewThreadDialogPullRequest | null;
  disabled?: boolean;
  /** Numbers that already have a thread in this project, so the list can say so. */
  linkedNumbers: ReadonlySet<number>;
  onChange: (pullRequest: NewThreadDialogPullRequest | null) => void;
}

const toSelection = (entry: PullRequestListEntry): NewThreadDialogPullRequest => ({
  projectId: entry.projectId,
  number: entry.number,
  headBranch: entry.headBranch,
  title: entry.title,
});

export function NewThreadDialogPullRequestField({
  projectId,
  value,
  disabled,
  linkedNumbers,
  onChange,
}: NewThreadDialogPullRequestFieldProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  // A project switch invalidates the previous project's answer — otherwise the field would
  // briefly offer another repository's pull requests.
  useEffect(() => {
    setQuery("");
  }, [projectId]);

  // `enabled` on open rather than on mount: most threads are not opened on a pull request, and
  // this would otherwise cost a GitHub read every time the dialog appears.
  const listQuery = useQuery({
    ...pullRequestsListQueryOptions({
      state: "open",
      projectId,
      page: 1,
      perPage: PICKER_PER_PAGE,
    }),
    enabled: open,
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });

  const entries = useMemo(
    () => listQuery.data?.entries.filter((entry) => entry.projectId === projectId) ?? [],
    [listQuery.data, projectId],
  );

  /**
   * The project's own failure, if it came back as data. A listing answers per project, so a
   * missing token is an entry in `errors[]` and not a thrown query.
   */
  const projectError = useMemo(
    () => listQuery.data?.errors.find((entry) => entry.projectId === projectId) ?? null,
    [listQuery.data, projectId],
  );

  const visibleEntries = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle.length === 0) return entries;
    return entries.filter(
      (entry) =>
        String(entry.number).includes(needle) ||
        entry.title.toLowerCase().includes(needle) ||
        entry.headBranch.toLowerCase().includes(needle),
    );
  }, [entries, query]);

  const entryByKey = useMemo(
    () => new Map(entries.map((entry) => [String(entry.number), entry] as const)),
    [entries],
  );
  const allItemValues = useMemo(
    () => [NO_PULL_REQUEST, ...entries.map((entry) => String(entry.number))],
    [entries],
  );
  const visibleItemValues = useMemo(
    () => [NO_PULL_REQUEST, ...visibleEntries.map((entry) => String(entry.number))],
    [visibleEntries],
  );

  const triggerLabel = value ? `#${value.number} — ${value.title}` : "Sin vincular";
  const loading = listQuery.isPending && open;
  const failure =
    projectError?.message ??
    (listQuery.error ? readPullRequestErrorMessage(listQuery.error, "") : "");

  return (
    <Combobox
      items={allItemValues}
      filteredItems={visibleItemValues}
      value={value ? String(value.number) : NO_PULL_REQUEST}
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setQuery("");
      }}
    >
      <ComboboxTrigger
        render={
          <Button
            type="button"
            variant="outline"
            className="w-full justify-start font-normal"
            disabled={disabled}
          />
        }
      >
        <span className="truncate">{triggerLabel}</span>
      </ComboboxTrigger>
      {/* Parenthesis and not brackets: this codebase is on Tailwind v4, where `w-[--anchor-width]`
          is silently dropped and the popup grows to fit its widest line — a long GitHub error
          stretches it across the whole window. */}
      <ComboboxPopup align="start" className="w-(--anchor-width)">
        <div className="flex items-center gap-2 border-b p-1">
          <ComboboxInput
            className="flex-1 rounded-md border-border/60 bg-background shadow-none before:hidden has-focus-visible:border-neutral-500/15 has-focus-visible:ring-0"
            inputClassName="ring-0"
            placeholder="Buscar por número, título o rama…"
            showTrigger={false}
            size="sm"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>

        {failure.length > 0 ? (
          <p className="px-3 py-2 text-xs text-destructive">{failure}</p>
        ) : loading ? (
          <div className="flex items-center gap-2 px-3 py-4 text-xs text-muted-foreground">
            <LoaderIcon className="size-3.5 animate-spin" />
            Cargando pull requests…
          </div>
        ) : (
          <>
            <ComboboxEmpty>
              {entries.length === 0
                ? "Este repositorio no tiene pull requests abiertos."
                : "Ningún pull request coincide con la búsqueda."}
            </ComboboxEmpty>
            <ComboboxList className="max-h-64">
              <ComboboxItem
                hideIndicator
                index={0}
                value={NO_PULL_REQUEST}
                onClick={() => onChange(null)}
                className="text-muted-foreground"
              >
                Sin vincular
              </ComboboxItem>
              {visibleEntries.map((entry, index) => (
                <ComboboxItem
                  hideIndicator
                  key={entry.number}
                  index={index + 1}
                  value={String(entry.number)}
                  onClick={() => {
                    const picked = entryByKey.get(String(entry.number));
                    if (picked) onChange(toSelection(picked));
                  }}
                >
                  <div className="flex w-full flex-col items-start gap-0.5 py-0.5">
                    <span className="flex w-full items-center gap-2">
                      <span className="font-mono text-[11px] text-muted-foreground">
                        #{entry.number}
                      </span>
                      {entry.isDraft ? (
                        <span className="rounded-full bg-muted/60 px-2 py-0.5 text-[10px] text-muted-foreground">
                          Borrador
                        </span>
                      ) : null}
                      {linkedNumbers.has(entry.number) ? (
                        <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] text-amber-600 dark:text-amber-500">
                          Ya tiene hilo
                        </span>
                      ) : null}
                    </span>
                    <span className="w-full truncate text-sm">{entry.title}</span>
                    <span className="w-full truncate font-mono text-[10px] text-muted-foreground">
                      {entry.headBranch}
                    </span>
                  </div>
                </ComboboxItem>
              ))}
            </ComboboxList>
          </>
        )}
      </ComboboxPopup>
    </Combobox>
  );
}
